import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { quoteRequestService } from '../services/quoteRequestService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../hooks/useAuth';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { formatDateDisplay } from '../utils/dateUtils';
import LineItemPartAutocomplete from '../components/LineItemPartAutocomplete';
import LineItemServiceAutocomplete from '../components/LineItemServiceAutocomplete';

const LIST_FILTERS_DEFAULTS = {
  search: '',
  status: 'submitted,in_review'
};

const toApproveItem = (it) => ({
  item_type: it.item_type === 'service' ? 'service' : 'part',
  part_id: it.part_id ? String(it.part_id) : '',
  service_id: it.service_id ? String(it.service_id) : '',
  description: it.description || '',
  quantity: it.quantity ?? 1,
  unit_price: it.unit_price ?? 0,
  unit: it.unit || (it.item_type === 'service' ? 'HR' : 'EA'),
  notes: it.notes || '',
});

const isMapped = (it) => (it.item_type === 'service' ? !!it.service_id : !!it.part_id);

const STATUS_COLORS = {
  draft: 'default',
  submitted: 'info',
  in_review: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'default',
};

const QuoteRequests = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_quote_requests_list_filters', LIST_FILTERS_DEFAULTS);
  const search = listFilters.search;
  const setSearch = useCallback((v) => setListFilter('search', v), [setListFilter]);
  const status = listFilters.status;
  const setStatus = useCallback((v) => setListFilter('status', v), [setListFilter]);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [accountRepId, setAccountRepId] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [approveItems, setApproveItems] = useState([]);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeService.getAll({ limit: 500 });
        const list = res.data || res || [];
        setEmployees(Array.isArray(list) ? list.filter((e) => e.is_active !== false) : []);
      } catch {
        /* optional */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await quoteRequestService.getAll({
        search: search || undefined,
        status: status || undefined,
        limit: 200,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!routeId) {
      setSelected(null);
      return;
    }
    (async () => {
      try {
        const res = await quoteRequestService.getById(routeId);
        setSelected(res.data);
        setAccountRepId(
          res.data.sales_rep_employee_id ? String(res.data.sales_rep_employee_id) : ''
        );
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load request');
      }
    })();
  }, [routeId]);

  const openDetail = async (row) => {
    navigate(`/quote-requests/${row.id}`);
  };

  const openApprove = () => {
    setApproveItems((selected?.items || []).map(toApproveItem));
    setApproveOpen(true);
  };

  const updateApproveItem = (index, patch) => {
    setApproveItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const handleItemTypeChange = (index, itemType) => {
    updateApproveItem(index, {
      item_type: itemType,
      part_id: '',
      service_id: '',
      unit: itemType === 'service' ? 'HR' : 'EA',
    });
  };

  const handleCatalogPick = (index, value, record) => {
    const row = approveItems[index];
    if (!value || !record) {
      updateApproveItem(index, { part_id: '', service_id: '' });
      return;
    }
    const isService = row.item_type === 'service';
    const catalogDescription = isService
      ? record.service_description || record.description || ''
      : record.part_description || record.description || '';
    updateApproveItem(index, {
      part_id: isService ? '' : String(record.id),
      service_id: isService ? String(record.id) : '',
      description: row.description || catalogDescription,
      unit_price: Number(row.unit_price) > 0 ? row.unit_price : record.unit_price || 0,
      unit: record.unit || row.unit,
    });
  };

  const unmappedCount = approveItems.filter((it) => !isMapped(it)).length;
  const mappedTotal = approveItems
    .filter(isMapped)
    .reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const handleApprove = async () => {
    if (!selected) return;
    setActing(true);
    setError('');
    try {
      const payload = {
        account_rep_employee_id: accountRepId ? parseInt(accountRepId, 10) : null,
        review_notes: reviewNotes || null,
      };
      if (approveItems.length > 0) {
        payload.items = approveItems.map((it) => ({
          item_type: it.item_type,
          part_id: it.item_type === 'part' && it.part_id ? parseInt(it.part_id, 10) : null,
          service_id: it.item_type === 'service' && it.service_id ? parseInt(it.service_id, 10) : null,
          description: it.description,
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          unit: it.unit,
          notes: it.notes || null,
        }));
      }
      const res = await quoteRequestService.approve(selected.id, payload);
      setApproveOpen(false);
      setReviewNotes('');
      await load();
      if (res.quote?.id) {
        navigate(`/quotes/${res.quote.id}`);
      } else {
        navigate('/quote-requests');
        setSelected(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    setActing(true);
    setError('');
    try {
      await quoteRequestService.reject(selected.id, { review_notes: reviewNotes || null });
      setRejectOpen(false);
      setReviewNotes('');
      setSelected(null);
      navigate('/quote-requests');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  const targetLabel = (row) => {
    if (row.lead_id) return `Lead #${row.lead_number} — ${row.lead_company_name || ''}`;
    if (row.customer_id) return `Customer #${row.customer_number} — ${row.customer_company_name || ''}`;
    return '—';
  };

  const repName = (row) =>
    `${row.sales_rep_first_name || ''} ${row.sales_rep_last_name || ''}`.trim() || '—';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Quote Requests</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="submitted,in_review">Pending Review</MenuItem>
              <MenuItem value="">All</MenuItem>
              {Object.keys(STATUS_COLORS).map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilters ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={clearListFilters}
            >
              Clear filters
            </Button>
          ) : null}
          <Button variant="outlined" onClick={load}>Refresh</Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {selected && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              Request #{selected.request_number}{' '}
              <Chip size="small" label={selected.status} color={STATUS_COLORS[selected.status] || 'default'} />
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => { setSelected(null); navigate('/quote-requests'); }}>Close</Button>
              {isAdmin() && ['submitted', 'in_review'].includes(selected.status) && (
                <>
                  <Button color="error" variant="outlined" onClick={() => setRejectOpen(true)}>Reject</Button>
                  <Button color="success" variant="contained" onClick={openApprove}>Approve</Button>
                </>
              )}
              {selected.created_quote_id && (
                <Button variant="outlined" onClick={() => navigate(`/quotes/${selected.created_quote_id}`)}>
                  Open Quote
                </Button>
              )}
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">For</Typography>
              <Typography>{targetLabel(selected)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Sales Rep</Typography>
              <Typography>{repName(selected)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Submitted</Typography>
              <Typography>{selected.submitted_at ? formatDateDisplay(selected.submitted_at) : '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Needed By</Typography>
              <Typography>{selected.needed_by_date ? formatDateDisplay(selected.needed_by_date) : '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Requested Total</Typography>
              <Typography>
                {selected.requested_total_amount != null
                  ? `$${Number(selected.requested_total_amount).toFixed(2)}`
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography whiteSpace="pre-wrap">{selected.description || '—'}</Typography>
            </Grid>
            {selected.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography whiteSpace="pre-wrap">{selected.notes}</Typography>
              </Grid>
            )}
            {selected.review_notes && (
              <Grid item xs={12}>
                <Alert severity="info">Review notes: {selected.review_notes}</Alert>
              </Grid>
            )}
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Line Items</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selected.items || []).length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">No line items</TableCell></TableRow>
              ) : (selected.items || []).map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.description || '—'}</TableCell>
                  <TableCell>{it.quantity}</TableCell>
                  <TableCell>{it.unit || '—'}</TableCell>
                  <TableCell>${Number(it.unit_price || 0).toFixed(2)}</TableCell>
                  <TableCell>${Number(it.line_total || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Request #</TableCell>
                <TableCell>For</TableCell>
                <TableCell>Sales Rep</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No quote requests</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                  <TableCell>{row.request_number}</TableCell>
                  <TableCell>{targetLabel(row)}</TableCell>
                  <TableCell>{repName(row)}</TableCell>
                  <TableCell>{row.description || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={STATUS_COLORS[row.status] || 'default'} />
                  </TableCell>
                  <TableCell>{row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        fullWidth
        maxWidth={approveItems.length > 0 ? 'lg' : 'sm'}
      >
        <DialogTitle>Approve Quote Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will create a real Quote
            {selected?.lead_id ? ' and convert the Lead into a Customer' : ''}.
          </Typography>
          {approveItems.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Map Line Items</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Pick a catalog part or service for each line so it lands on the quote as a real line item.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={110}>Type</TableCell>
                    <TableCell width={300}>Catalog Item</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell width={90}>Qty</TableCell>
                    <TableCell width={80}>Unit</TableCell>
                    <TableCell width={120}>Unit Price</TableCell>
                    <TableCell width={100} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approveItems.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={it.item_type}
                          onChange={(e) => handleItemTypeChange(idx, e.target.value)}
                        >
                          <MenuItem value="part">Part</MenuItem>
                          <MenuItem value="service">Service</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {it.item_type === 'service' ? (
                          <LineItemServiceAutocomplete
                            valueId={it.service_id}
                            onCommit={(v, rec) => handleCatalogPick(idx, v, rec)}
                            noMargin
                          />
                        ) : (
                          <LineItemPartAutocomplete
                            valueId={it.part_id}
                            onCommit={(v, rec) => handleCatalogPick(idx, v, rec)}
                            showNewPart={false}
                            noMargin
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={it.description}
                          onChange={(e) => updateApproveItem(idx, { description: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={it.quantity}
                          onChange={(e) => updateApproveItem(idx, { quantity: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={it.unit}
                          onChange={(e) => updateApproveItem(idx, { unit: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={it.unit_price}
                          onChange={(e) => updateApproveItem(idx, { unit_price: e.target.value })}
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
                Quote subtotal from mapped lines: <strong>${mappedTotal.toFixed(2)}</strong>
              </Typography>
              {unmappedCount > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {unmappedCount} line{unmappedCount === 1 ? '' : 's'} not mapped to a catalog item.
                  {' '}Unmapped lines are added to the quote notes instead of as line items.
                </Alert>
              )}
            </Box>
          )}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Account Rep</InputLabel>
            <Select
              label="Account Rep"
              value={accountRepId}
              onChange={(e) => setAccountRepId(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={String(e.id)}>
                  {e.first_name} {e.last_name}
                  {e.role === 'sales_rep' ? ' (Sales Rep)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth size="small" multiline minRows={2} label="Review Notes (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={acting}>
            Approve & Create Quote
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject Quote Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" multiline minRows={3} label="Rejection Notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={acting}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteRequests;
